import { createReducer } from '@reduxjs/toolkit';
import { FieldLayout } from '../../pages/map/types/FieldLayout';
import { getPaddedBBox } from '../../pages/map/service/common/utils';
import * as turf from '@turf/turf';
import { Feature } from '@turf/helpers';
import { EnumProcessingStatus, EnumProcessingStatusType } from '../../pages/map/service/common/types';
import { ShapefileStatus } from '../../pages/map/types/Shapefiles';
import { MAP_MODES } from '../../pages/map/components/Drawing/modes/modes';
import DrawControl from '../../pages/map/components/Map/DrawControl';
import { kmToAcre, kmToHectare } from '../../pages/map/service/common/constants';
import { polygonArea } from '../../pages/map/service/common/AreaCalc';
import i18next from 'i18next';
import mapboxgl from 'mapbox-gl';
import { v4, v4 as uuidv4 } from 'uuid';
import { fitBoundsOption, SF_PRACTICES_MAP, states, SyncErrors } from '../actions/map-actions/field/field-helper';
import { MultiPolygon, Polygon } from '@turf/turf';
import { ClassificationLayersStatus, LayerOption } from '../../service/common/constants';

export interface ValidationResult {
  selfIntersections: {
    field_id: string;
    valid: boolean;
    reason?: string;
    location?: {
      type: string;
      coordinates: number[];
    };
  }[];
  intersectionsBetweenFields: {
    field_id: string;
    intersects_with: string;
    intersection: {
      type: string;
      coordinates: number[];
    };
  }[];
}

const verifyFieldsIntersections = (fields: Feature[], intersections: ValidationResult) => {
  const intersectFields = [...fields]
    .map((f) => {
      return {
        ...f,
        properties: {
          ...f.properties,
          intersects: false,
        },
      } as Feature;
    })
    .filter((fd) => !fd.properties?.unenrolledAt);

  const selfOverlappedIds = intersections.selfIntersections?.filter((inters) => !inters?.valid).map((invalid) => invalid?.field_id) ?? [];

  for (const intersectField of intersectFields) {
    intersectField.properties = intersectField.properties ?? {};
    if (selfOverlappedIds?.includes(String(intersectField.id))) {
      intersectField.properties.intersects = true;
    }

    if (intersections.intersectionsBetweenFields?.find((inter) => [inter.field_id, inter.intersects_with].includes(String(intersectField.id)))) {
      intersectField.properties.intersects = true;
    }
  }

  // Inserting unenrolled fields back to list, since we do not care for their overlaps
  return [...intersectFields, ...fields.filter((f) => !!f.properties?.unenrolledAt)];
};

const verifyOverlapsIntersections = (fields: Feature[], overlaps: { intersectsWith: string }[]) => {
  const intersectFields = [...fields]
    .map((f) => {
      return {
        ...f,
        properties: {
          ...f.properties,
          overlaps: false,
        },
      } as Feature;
    })
    .filter((fd) => !fd.properties?.unenrolledAt);

  for (const intersectField of intersectFields) {
    intersectField.properties = intersectField.properties ?? {};
    if (overlaps.find((overlap) => overlap.intersectsWith === String(intersectField.id))) {
      intersectField.properties.overlaps = true;
    }
  }

  // Inserting unenrolled fields back to list, since we do not care for their overlaps
  return [...intersectFields, ...fields.filter((f) => !!f.properties?.unenrolledAt)];
};

const calculateFieldArea = (field: Feature, hectare = false) => {
  const areaConversion = hectare ? kmToHectare : kmToAcre;

  if (field.geometry?.type === 'MultiPolygon') {
    return (field.geometry.coordinates as turf.helpers.Position[][][]).reduce((i: number, coordinates: number[][][]) => {
      return i + polygonArea(coordinates) * areaConversion;
    }, 0);
  } else {
    return polygonArea((field.geometry as turf.helpers.Geometry).coordinates as number[][][]) * areaConversion;
  }
};

const centerMap = (fields: Feature[], map?: mapboxgl.Map) => {
  if (!map) {
    return;
  }
  const newCenter = getPaddedBBox(
    turf.bbox(turf.featureCollection(fields.map((f) => f.properties?.processedIneligibleBoundaries?.original.boundaries ?? f))),
  );
  map.fitBounds([newCenter[0], newCenter[1], newCenter[2], newCenter[3]], fitBoundsOption);
};

const treatPractices = (f: Feature, practice, appliedPractices, areaKey, paymentOptionBR) => {
  const practiceCode =
    practice === 'ICL' ? SF_PRACTICES_MAP[practice + (f.properties?.practiceType === 'PASTURE' ? '1' : '2')] : SF_PRACTICES_MAP[practice];
  const paymentOption = ['NO', 'NRR'].indexOf(practiceCode) >= 0 ? 'ANNUAL' : f.properties?.paymentOption || paymentOptionBR;
  let currentPlatformPractice = appliedPractices.find((i) => practiceCode === i.name && paymentOption === i.option);
  if (!currentPlatformPractice) {
    currentPlatformPractice = { name: practiceCode, sf: 0, platform: f.properties?.[areaKey], option: paymentOption };
    appliedPractices.push(currentPlatformPractice);
  } else {
    currentPlatformPractice.platform += f.properties?.[areaKey];
  }
};

const combineFeatures = (allFeatures: Feature<Polygon | MultiPolygon>[]): Feature<Polygon | MultiPolygon> => {
  return allFeatures.length === 1
    ? allFeatures[0]
    : allFeatures.slice(1).reduce((p: any, c) => {
        if (p.geometry.type === 'MultiPolygon') {
          if (c.geometry.type === 'MultiPolygon') {
            p.geometry.coordinates.push(...c.geometry.coordinates);
          } else {
            p.geometry.coordinates.push(c.geometry.coordinates);
          }
        } else {
          p.geometry.type = 'MultiPolygon';
          p.geometry.coordinates = [p.geometry.coordinates];
          if (c.geometry.type === 'MultiPolygon') {
            p.geometry.coordinates.push(...c.geometry.coordinates);
          } else {
            p.geometry.coordinates.push(c.geometry.coordinates);
          }
        }
        return p;
      }, allFeatures[0]);
};

interface MapState {
  state: states;
  layout: FieldLayout | undefined;
  map: mapboxgl.Map | undefined;
  mode: string; // MAP_MODES
  drawControl: DrawControl | undefined;
  initialLoad: boolean;

  fields: Feature[];
  processedFields: Feature[];
  originalFields: Record<string, Feature>;
  showRemoved: boolean;

  isDrawingTraining: boolean;
  trainingRegionsTouched: boolean;
  trainingRegions: Feature[];
  showTrainigngRegions: boolean;
  classificationState: ClassificationLayersStatus;
  classification_data?: any;
  classificationLayers?: {
    cdl_landcover: any;
    classified: any;
    classified_dw: any;
    esa_landcover: any;
  };
  removeFromFieldLoading: boolean;
  removedFromfields: string[];

  intersections: ValidationResult;
  fieldsToCheckIntersection: Feature[];
  loadingFields: boolean;
  finishedDrawingFields: boolean;
  fieldsDrawingInvalid: boolean;
  totalFieldsArea: { areaAcre: number; areaHectare: number };
  deletedFields: Feature[];
  lastFieldNum: number;
  fieldsToCheckLocation: Feature[];

  paddocks: { [key: string]: Feature[] };
  paddocksBeingDrawn: Feature[];
  finishedDrawingPaddocks: boolean;

  updatedFieldBoundaries: boolean;
  shapefileStatus: ShapefileStatus;
  loadingShapefileStatus: boolean;
  showCloserFields: boolean;
  uploadingBoundaries: boolean;
  uploadingDrawnField: boolean;

  overlap: any[] | undefined;
  loadingOverlap: boolean;
  nearFields: any[];
  loadingNearFields: boolean;
  checkOverlaps: boolean;
  processingStatus: EnumProcessingStatusType;
  ineligibleAreasFinalized: boolean;
  enableFinalize: boolean;

  estimate: any | undefined;
  practicesOnContract: any[] | undefined;
  contractPaymentOption: string | undefined;
  contractVersion: any | undefined;
  contractInSF: any | undefined;
  syncErrors: SyncErrors;

  // Grazing stuff (disabled for now)
  events: any[];
  structures: any[];
  herds: any[];

  // Classification POC
  classificationLayer?: LayerOption;
}

const initialState: MapState = {
  state: 'OVERVIEW_V2',
  layout: undefined,
  map: undefined,
  mode: MAP_MODES.STATIC,
  drawControl: undefined,
  initialLoad: false,
  fields: [],
  originalFields: {},
  processedFields: [],
  trainingRegions: [],
  removeFromFieldLoading: false,
  removedFromfields: [],
  isDrawingTraining: false,
  trainingRegionsTouched: false,
  showRemoved: false,
  showTrainigngRegions: true,
  intersections: {
    intersectionsBetweenFields: [],
    selfIntersections: [],
  },
  fieldsToCheckIntersection: [],
  loadingFields: true,
  finishedDrawingFields: false,
  fieldsDrawingInvalid: false,
  totalFieldsArea: {
    areaAcre: 0,
    areaHectare: 0,
  },
  deletedFields: [],
  lastFieldNum: 1,
  fieldsToCheckLocation: [],
  paddocks: {},
  paddocksBeingDrawn: [],
  finishedDrawingPaddocks: true,
  updatedFieldBoundaries: false,
  shapefileStatus: 'PENDING',
  loadingShapefileStatus: true,
  showCloserFields: true,
  uploadingBoundaries: false,
  uploadingDrawnField: false,
  overlap: undefined,
  loadingOverlap: false,
  nearFields: [],
  loadingNearFields: false,
  checkOverlaps: false,
  processingStatus: EnumProcessingStatus.NOT_PROCESSED,
  ineligibleAreasFinalized: false,
  enableFinalize: false,
  estimate: undefined,
  practicesOnContract: undefined,
  contractPaymentOption: undefined,
  contractVersion: undefined,
  contractInSF: undefined,
  syncErrors: { practices: [], newPractices: [], paymentOptionErrors: [], missingPractices: [], status: 'UNKNOWN' },

  classificationState: ClassificationLayersStatus.PENDINDG,

  events: [],
  structures: [],
  herds: [],

  classificationLayer: undefined,
};

const mapReducer = createReducer(initialState, {
  SET_CLASSIFICATION_LAYER: (state, action) => {
    state.classificationLayer = action.payload;
  },

  GET_CLASSIFICATION_LAYERS_REQUESTED: (state) => {
    state.classificationLayers = undefined;
    state.classification_data = undefined;
  },

  GET_CLASSIFICATION_LAYERS_SUCCEEDED: (state, action) => {
    state.classificationLayers = action.payload.layers;
    state.classification_data = action.payload.classification_data;
    state.classificationState = ClassificationLayersStatus.SUCCESS;
    state.processedFields = action.payload.processed;
  },

  GET_CLASSIFICATION_LAYERS_UNAVAILABLE: (state) => {
    state.classificationState = ClassificationLayersStatus.UNAVAILABLE;
    state.classification_data = undefined;
  },

  GET_CLASSIFICATION_LAYERS_LOADING: (state) => {
    state.classificationState = ClassificationLayersStatus.LOADING;
  },

  GET_CLASSIFICATION_LAYERS_GENERATING: (state) => {
    state.classificationState = ClassificationLayersStatus.GENERATING;
  },

  GET_CLASSIFICATION_LAYERS_ERRORED: (state, action) => {
    state.classificationLayers = action.payload.layers;
    state.classification_data = undefined;
    state.classificationState = ClassificationLayersStatus.ERROR;
  },

  TOGGLE_IS_DRAWING_TRAINING: (state) => {
    state.isDrawingTraining = !state.isDrawingTraining;
    state.showTrainigngRegions = true;
  },

  TOGGLE_REMOVE_TREES: (state) => {
    state.showRemoved = !state.showRemoved;
  },

  TOGGLE_IS_TRAINING_VISIBLE: (state, action) => {
    if (typeof action.payload === 'boolean') {
      state.showTrainigngRegions = action.payload;
    } else {
      state.showTrainigngRegions = !state.showTrainigngRegions;
    }
  },

  EDIT_TRAINING: (state, action) => {
    const { id, newClass } = action.payload;
    const region = state.trainingRegions.find((r) => r.id === id);
    if (region && region.properties) {
      region.properties.class = newClass;
      state.trainingRegionsTouched = true;
    }
  },

  DELETE_TRAINING: (state, action) => {
    const { id } = action.payload;
    state.trainingRegionsTouched = true;
    state.trainingRegions = state.trainingRegions?.filter((r) => r.id !== id);
  },

  GET_TRAINING_SUCCEEDED: (state, action) => {
    state.trainingRegions = action.payload;
    state.trainingRegionsTouched = false;
  },

  CLEAR_DATA: (state) => {
    state.state = initialState.state;
    state.layout = initialState.layout;
    state.map = initialState.map;
    state.mode = initialState.mode;
    state.drawControl = undefined;
    state.initialLoad = initialState.initialLoad;
    state.fields = initialState.fields;
    state.intersections = initialState.intersections;
    state.fieldsToCheckIntersection = initialState.fieldsToCheckIntersection;
    state.loadingFields = initialState.loadingFields;
    state.finishedDrawingFields = initialState.finishedDrawingFields;
    state.fieldsDrawingInvalid = initialState.fieldsDrawingInvalid;
    state.deletedFields = initialState.deletedFields;
    state.lastFieldNum = initialState.lastFieldNum;
    state.paddocks = initialState.paddocks;
    state.paddocksBeingDrawn = initialState.paddocksBeingDrawn;
    state.finishedDrawingPaddocks = initialState.finishedDrawingPaddocks;
    state.updatedFieldBoundaries = initialState.updatedFieldBoundaries;
    state.shapefileStatus = initialState.shapefileStatus;
    state.loadingShapefileStatus = initialState.loadingShapefileStatus;
    state.showCloserFields = initialState.showCloserFields;
    state.uploadingBoundaries = initialState.uploadingBoundaries;
    state.uploadingDrawnField = initialState.uploadingDrawnField;
    state.overlap = initialState.overlap;
    state.loadingOverlap = initialState.loadingOverlap;
    state.nearFields = initialState.nearFields;
    state.loadingNearFields = initialState.loadingNearFields;
    state.checkOverlaps = initialState.checkOverlaps;
    state.processingStatus = initialState.processingStatus;
    state.ineligibleAreasFinalized = initialState.ineligibleAreasFinalized;
    state.enableFinalize = initialState.enableFinalize;
    state.estimate = initialState.estimate;
    state.practicesOnContract = initialState.practicesOnContract;
    state.contractPaymentOption = initialState.contractPaymentOption;
    state.contractVersion = initialState.contractVersion;
    state.contractInSF = initialState.contractInSF;
    state.syncErrors = initialState.syncErrors;

    state.events = initialState.events;
    state.structures = initialState.structures;
    state.herds = initialState.herds;
  },
  SET_MAP_STATE: (state, action) => {
    state.state = action.payload;
    state.initialLoad = true;
  },
  SET_LAYOUT: (state, action) => {
    state.layout = action.payload;
  },
  MAP_LOADED: (state, action) => {
    state.map = action.payload;
    if (!state.fields.length || !state.map) {
      return;
    }
    const newCenter = getPaddedBBox(
      turf.bbox(turf.featureCollection(state.fields.map((f) => f.properties?.processedIneligibleBoundaries?.original.boundaries ?? f))),
    );
    state.map.fitBounds([newCenter[0], newCenter[1], newCenter[2], newCenter[3]], fitBoundsOption);
  },
  LOADING_FIELDS: (state) => {
    state.loadingFields = true;
  },
  SET_UPLOADING_DRAWN_FIELD: (state, action) => {
    state.uploadingDrawnField = action.payload;
  },
  GET_FIELDS_REQUESTED: (state) => {
    state.loadingFields = true;
    state.fields = [];
  },
  GET_FIELDS_SUCCEEDED: (state, action) => {
    const { fields, paddocks } = action.payload;

    state.fields = fields;
    // save fields as original to be used when editing
    for (const f of fields) {
      state.originalFields[f.id as string] = f;
    }
    state.paddocks = paddocks;
    state.deletedFields = [];

    centerMap(state.fields, state.map);
    state.fieldsToCheckIntersection.push(...fields.filter((f) => !f.properties?.unenrolledAt));
    state.loadingFields = false;
    state.checkOverlaps = true;

    const hasNotProcessed = state.fields.some((f) => !f.properties?.ineligibleAreasProcessed);
    state.processingStatus = !hasNotProcessed ? EnumProcessingStatus.PROCESSED : EnumProcessingStatus.NOT_PROCESSED;
    state.ineligibleAreasFinalized = !!state.fields.length && !state.fields.some((b) => !b.properties?.ineligibleAreasFinalized);
  },
  GET_FIELDS_ERRORED: (state) => {
    state.fields = [];
    state.loadingFields = false;
  },

  REMOVE_FROM_FIELD_REQUESTED: (state, action) => {
    state.removeFromFieldLoading = true;
    if (!state.removedFromfields.includes(action.payload.id)) {
      state.removedFromfields.push(action.payload.id);
    }
  },

  REMOVE_FROM_FIELD_SUCCEEDED: (state, action) => {
    state.removeFromFieldLoading = false;
    state.fields.find((f) => f.id === action.payload.id)!.geometry = action.payload.data.geometry;
  },

  RESET_FIELD: (state, action) => {
    if (state.removedFromfields.includes(action.payload.id)) {
      console.log('sss');
      state.removedFromfields = state.removedFromfields.splice(state.removedFromfields.indexOf(action.payload.id), 1);
    }
    state.fields.find((f) => f.id === action.payload.id)!.geometry = state.originalFields[action.payload.id].geometry;
  },

  GET_SHAPEFILES_REQUESTED: (state) => {
    state.loadingShapefileStatus = true;
    state.shapefileStatus = 'PENDING';
  },
  GET_SHAPEFILES_SUCCEEDED: (state, action) => {
    state.loadingShapefileStatus = false;
    state.shapefileStatus = action.payload;
  },
  GET_SHAPEFILES_ERRORED: (state) => {
    state.loadingShapefileStatus = false;
    state.shapefileStatus = 'PENDING';
  },
  SHOW_CLOSER_FIELDS: (state, action) => {
    state.showCloserFields = action.payload;
  },
  SET_PROCESSING_STATUS: (state, action) => {
    state.processingStatus = action.payload;
  },
  SET_INELIGIBLE_AREAS: (state, action) => {
    state.ineligibleAreasFinalized = action.payload;
  },
  SET_UPLOADING_BOUNDARIES: (state, action) => {
    state.uploadingBoundaries = action.payload;
  },
  VERIFY_OVERLAP_REQUESTED: (state) => {
    state.checkOverlaps = false;
    state.loadingOverlap = true;
  },
  VERIFY_OVERLAP_SUCCEEDED: (state, action) => {
    state.overlap = action.payload;
    state.fields = verifyOverlapsIntersections(state.fields, action.payload);
    state.loadingOverlap = false;
  },
  VERIFY_OVERLAP_ERRORED: (state) => {
    state.overlap = undefined;
    state.checkOverlaps = false;
    state.loadingOverlap = false;
  },
  VERIFY_NEAR_FIELDS_REQUESTED: (state) => {
    state.loadingNearFields = true;
  },
  VERIFY_NEAR_FIELDS_SUCCEEDED: (state, action) => {
    state.nearFields = action.payload;
    state.loadingNearFields = false;
  },
  VERIFY_NEAR_FIELDS_ERRORED: (state) => {
    state.nearFields = [];
    state.loadingNearFields = false;
  },
  ENABLE_FINALIZE: (state) => {
    state.enableFinalize = true;
  },
  GET_MAP_ESTIMATE_REQUESTED: (state) => {
    state.estimate = undefined;
  },
  GET_MAP_ESTIMATE_SUCCEEDED: (state, action) => {
    state.estimate = action.payload;
  },
  GET_MAP_ESTIMATE_ERRORED: (state) => {
    state.estimate = undefined;
  },
  SET_DRAW_CONTROL: (state, action) => {
    state.drawControl = action.payload;
  },
  RESTART_DRAW: (state) => {
    if (!state.drawControl) {
      return;
    }

    state.drawControl.draw?.deleteAll();
  },
  LAST_FIELD_NUM_REQUESTED: (state) => {
    state.lastFieldNum = 1;
  },
  LAST_FIELD_NUM_SUCCEEDED: (state, action) => {
    state.lastFieldNum = action.payload;
  },
  LAST_FIELD_NUM_ERRORED: (state) => {
    state.lastFieldNum = 1;
  },
  SET_TOTAL_FIELDS_AREA: (state, action) => {
    state.totalFieldsArea = action.payload;
  },
  ADD_FIELD: (state, action) => {
    state.uploadingBoundaries = false;

    if (action.payload?.length) {
      state.fields = [...(state.fields ?? []), ...action.payload];
      state.updatedFieldBoundaries = true;
      centerMap(state.fields, state.map);
      state.checkOverlaps = true;
      state.fieldsToCheckIntersection.push(...action.payload);
      return;
    }

    if (!state.drawControl || !state.drawControl.draw.getAll()?.features.length) {
      return;
    }

    const allFeatures = state.drawControl.draw.getAll().features;
    const newField = combineFeatures(allFeatures);

    if (!newField) {
      return;
    }

    newField.id = uuidv4();
    newField.properties = newField.properties ?? {};

    if (state.isDrawingTraining) {
      newField.properties.editing = false;
      newField.properties.class = 1;
      newField.properties.area = calculateFieldArea(newField);

      state.drawControl?.draw.changeMode(MAP_MODES.STATIC);
      state.mode = MAP_MODES.STATIC;

      state.drawControl.draw.deleteAll();
      console.log(state.trainingRegions);
      state.trainingRegions = [...(state.trainingRegions ?? []), newField];
      state.processingStatus = EnumProcessingStatus.NOT_PROCESSED;
      state.trainingRegionsTouched = true;
    } else {
      newField.properties.area = calculateFieldArea(newField);
      newField.properties.areaHectare = calculateFieldArea(newField, true);
      newField.properties.country = i18next.language === 'pt-BR' ? 'Brazil' : 'United States';
      newField.properties.area_unit = i18next.language === 'pt-BR' ? 'hectares' : 'acres';
      newField.properties.name = `${i18next.language === 'pt-BR' ? 'Talhão' : 'Field'} #${state.lastFieldNum + (state.fields?.length ?? 0)}`;
      newField.properties.situation = 'OWNED';
      newField.properties.practiceType = i18next.language === 'pt-BR' ? 'PASTURE' : 'ROW_CROP';
      newField.properties.paymentOption = undefined;
      newField.properties.irrigated = false;
      newField.properties.legume = false;
      newField.properties.previousTillage = 'intensive';
      newField.properties.practices = [];
      // The 'parent' property is used to reference the field for editing
      newField.properties.parent = newField.id;

      newField.properties.editing = false;

      state.updatedFieldBoundaries = true;
      state.drawControl?.draw.changeMode(MAP_MODES.STATIC);
      state.mode = MAP_MODES.STATIC;

      state.drawControl.draw.deleteAll();
      state.fields = [...state.fields, newField];
      state.fieldsToCheckLocation.push(newField);
      state.fieldsToCheckIntersection.push(newField);

      state.checkOverlaps = true;
      state.uploadingDrawnField = false;
      state.fieldsDrawingInvalid = false;
      state.processingStatus = EnumProcessingStatus.NOT_PROCESSED;
      state.ineligibleAreasFinalized = false;
    }
  },

  UPDATE_FIELD: (state, action) => {
    if (!state.drawControl) {
      return;
    }

    const features = state.drawControl.draw.getAll()?.features;

    if (features?.length || state.mode === MAP_MODES.AUTO_DIRECT_SELECT) {
      return;
    }

    const fieldId = typeof action.payload.field === 'string' ? action.payload.field : action.payload.field.id;
    state.fields = (state.fields ?? []).map((fieldSet) => {
      fieldSet.properties = fieldSet.properties ?? {};
      fieldSet.properties['editing'] = fieldSet.id === fieldId;
      if (fieldSet.id === fieldId) {
        if (action.payload.editMode === 'SPLIT') {
          if ((state.paddocks ?? {})[fieldId!]?.length) {
            state.paddocks[fieldId!].map((paddock) => state.drawControl!.draw.add(paddock));
          } else {
            state.drawControl!.draw.add(fieldSet);
          }

          state.drawControl?.draw.changeMode(MAP_MODES.SPLIT_FIELD, { featureId: fieldSet.id });
          state.mode = MAP_MODES.SPLIT_FIELD;
        } else if (action.payload.editMode === 'EDIT') {
          state.drawControl!.draw.add(fieldSet.properties.processedIneligibleBoundaries?.original?.boundaries ?? { ...fieldSet });

          state.drawControl!.draw.changeMode(MAP_MODES.AUTO_DIRECT_SELECT);
          state.mode = MAP_MODES.AUTO_DIRECT_SELECT;
        }
      }

      return fieldSet;
    });
  },
  UPDATE_FIELD_INFO: (state, action) => {
    state.updatedFieldBoundaries = true;
    if (!state.fields) {
      return;
    }
    const index = state.fields.findIndex((fieldSearch) => fieldSearch.id === action.payload.field.id);
    if (state.fields[index].properties?.paymentOption !== action.payload.field.properties?.paymentOption && i18next.language === 'pt-BR') {
      state.fields = state.fields.map((fieldOld) => {
        fieldOld.properties = fieldOld.properties ?? {};
        fieldOld.properties.paymentOption = action.payload.field.properties?.paymentOption;
        return fieldOld;
      });
    } else {
      state.fields[index] = action.payload.field;
    }
  },
  UPDATE_FIELD_BOUNDARY: (state) => {
    const editedField = (state.fields ?? []).find((field) => field.properties?.['editing']);

    if (!state.fields || !editedField || !state.drawControl?.draw.getAll()?.features.length) {
      return;
    }

    const allFeatures = state.drawControl.draw.getAll().features;
    const newField = combineFeatures(allFeatures);

    if (!newField) {
      state.uploadingDrawnField = false;
      return;
    }

    const sameBoundary =
      newField.geometry.coordinates.toString() ===
      (editedField.properties?.processedIneligibleBoundaries?.original?.boundaries || editedField).geometry.coordinates!.toString();
    if (sameBoundary) {
      (editedField.properties ?? {}).editing = false;
      (newField.properties ?? {}).editing = false;
      state.drawControl.draw.deleteAll();
      state.drawControl.draw.changeMode(MAP_MODES.STATIC);
      state.mode = MAP_MODES.STATIC;
      state.uploadingDrawnField = false;
      return;
    }

    state.updatedFieldBoundaries = true;

    newField.id = editedField.id;
    newField.properties = editedField.properties ?? {};
    newField.properties.area = calculateFieldArea(newField);
    newField.properties.areaHectare = calculateFieldArea(newField, true);

    if (newField.properties.processedIneligibleBoundaries?.original) {
      newField.properties.processedIneligibleBoundaries.original = {
        area: newField.properties.area,
        areaHectare: newField.properties.areaHectare,
        ineligibleAreaAcres: 0,
        ineligibleAreaHectares: 0,
        boundaries: {
          geometry: newField.geometry,
          type: 'Feature',
        },
      };
    }

    newField.properties.ineligibleAreasFinalized = false;
    state.processingStatus = EnumProcessingStatus.NOT_PROCESSED;
    state.ineligibleAreasFinalized = false;

    newField.properties.editing = false;

    state.drawControl?.draw.changeMode(MAP_MODES.STATIC);
    state.mode = MAP_MODES.STATIC;

    state.drawControl.draw.deleteAll();

    state.fields[state.fields.indexOf(editedField)] = newField;
    state.fieldsToCheckLocation.push(newField);
    state.fieldsToCheckIntersection.push(newField);

    state.checkOverlaps = true;
    state.uploadingDrawnField = false;
  },
  REMOVE_FIELD: (state, action) => {
    const deletedField = action.payload;
    state.updatedFieldBoundaries = true;
    state.deletedFields = [...(state.deletedFields ?? []), deletedField];
    state.intersections = {
      selfIntersections: state.intersections.selfIntersections.filter((field) => field.field_id !== deletedField.id),
      intersectionsBetweenFields: state.intersections.intersectionsBetweenFields.filter(
        (field) => field.field_id !== deletedField.id && field.intersects_with !== deletedField.id,
      ),
    };
    state.fields = verifyFieldsIntersections(
      state.fields.filter((f) => f.id !== deletedField.id),
      state.intersections,
    );
    if (state.fields.length === 0) {
      state.processingStatus = EnumProcessingStatus.NOT_PROCESSED;
      state.ineligibleAreasFinalized = false;
    }
    state.checkOverlaps = true;
  },
  REMOVE_ALL_FIELDS: (state) => {
    state.updatedFieldBoundaries = true;
    state.deletedFields = [...(state.deletedFields ?? []), ...(state.fields ?? [])];
    state.fields = [];
    state.nearFields = [];
    state.overlap = [];
    state.intersections = {
      selfIntersections: [],
      intersectionsBetweenFields: [],
    };
    state.processingStatus = EnumProcessingStatus.NOT_PROCESSED;
    state.ineligibleAreasFinalized = false;
    state.checkOverlaps = true;
  },
  SAVE_FIELDS_SUCCEEDED: (state) => {
    state.updatedFieldBoundaries = false;
  },
  DELETE_FIELDS: (state) => {
    state.deletedFields = [];
    state.processingStatus = EnumProcessingStatus.NOT_PROCESSED;
  },
  FOCUS_FIELD: (state, action) => {
    state.fields = (state.fields ?? []).map((field) => {
      field.properties = field.properties ?? {};
      field.properties['selected'] = field.id === action.payload;
      return field;
    });
  },
  HIDE_FIELDS: (state, action) => {
    state.fields = (state.fields ?? []).map((f) => {
      if (f.properties?.unenrolledAt) {
        f.properties.hidden = action.payload;
      }
      return f;
    });
  },
  GET_PRACTICES_ON_CONTRACT_SUCCEEDED: (state, action) => {
    state.practicesOnContract = action.payload;
  },
  GET_CONTRACT_PAYMENT_OPTION_SUCCEEDED: (state, action) => {
    state.contractPaymentOption = action.payload;
  },
  GET_CONTRACT_VERSION_SUCCEEDED: (state, action) => {
    state.contractVersion = action.payload;
  },
  GET_CONTRACT_IN_SF_SUCCEEDED: (state, action) => {
    state.contractInSF = action.payload;
  },
  UPDATE_PADDOCK: (state, action) => {
    const fieldId = action.payload.properties!['fieldId'];
    const paddockId = action.payload.id;

    const index = ((state.paddocks ?? {})[fieldId] ?? []).findIndex((paddock) => paddock.id === paddockId);
    if (index === -1 || !state.paddocks) {
      return;
    }

    state.paddocks[fieldId][index] = action.payload;
  },
  RESTART_PADDOCK: (state) => {
    if (!state.fields || !state.paddocks) {
      return;
    }

    let field = state.fields.find((field) => field.properties?.['editing']);
    if (!field) {
      field = state.fields.find((field) => field.properties?.['selected']);
    }
    if (!field) {
      return;
    }

    state.finishedDrawingPaddocks = true;
    state.paddocks[field.id!.toString()] = [];
  },
  SET_FINISHED_FIELDS: (state) => {
    if (!state.drawControl) return;
    const features = state.drawControl.draw.getAll().features ?? [];

    state.finishedDrawingFields = !(
      features.length === 0 ||
      features.some(
        (feat: any) =>
          !feat.geometry.coordinates.length || feat.geometry.coordinates.some((coordinates: any) => !coordinates.length || coordinates[0] === null),
      )
    );
    state.fieldsDrawingInvalid = !(
      (state.mode === MAP_MODES.STATIC || state.mode === MAP_MODES.AUTO_DIRECT_SELECT) &&
      (features.length === 0 || state.finishedDrawingFields)
    );
  },
  ON_MODE_CHANGE: (state, action) => {
    state.mode = action.payload;
    state.finishedDrawingFields = true;
    if (state.mode === MAP_MODES.SPLIT_FIELD) state.finishedDrawingPaddocks = true;
  },
  SET_MODE: (state, action) => {
    if (!state.drawControl || !state.map) {
      return;
    }

    if (state.drawControl.draw.getMode() === action.payload.mode) {
      return;
    }

    const features = state.drawControl.draw.getAll()?.features;
    const featureIds = (features ?? []).map((feature: any) => feature.id);

    const options: any = {};

    if (action.payload.mode !== MAP_MODES.CIRCULAR_BORDER) {
      if ([MAP_MODES.DIRECT_SELECT, MAP_MODES.INNER_BORDER, MAP_MODES.POINT_DELETION].includes(action.payload.mode)) {
        options.featureId = featureIds[featureIds.length - 1];
      } else if (featureIds.length > 1) {
        options.featureIds = featureIds;
      } else {
        options.featureId = featureIds[0];
      }
    }
    if ([MAP_MODES.DIRECT_SELECT, MAP_MODES.STATIC, MAP_MODES.SIMPLE_SELECT].includes(action.payload.mode)) {
      state.map.getCanvas().style.cursor = 'unset';
    } else {
      state.map.getCanvas().style.cursor = 'crosshair';
    }

    state.drawControl.draw.changeMode(action.payload.mode, options);
    state.mode = action.payload.mode;
  },
  ON_DRAW_UPDATE: (state, action) => {
    if (action.payload.action === 'CreatePaddockVertex') {
      state.finishedDrawingPaddocks = false;
      return;
    }

    if (!action.payload.features.length || action.payload.features.length === 1) {
      return;
    }

    const features = action.payload.features.map((feature: any) => {
      return {
        id: feature.id,
        type: 'Feature',
        geometry: {
          type: feature.type,
          coordinates: feature.coordinates,
        },
        properties: {
          ...feature.properties,
          fieldId: feature.properties.fieldId,
          fieldName: feature.properties.fieldName || feature.properties.name,
        },
      } as Feature;
    });

    state.paddocksBeingDrawn = features.map((newPaddock: Feature) => {
      newPaddock.properties = newPaddock.properties || {};
      newPaddock.id = uuidv4();
      newPaddock.properties['area'] = turf.area(newPaddock) * kmToAcre;
      newPaddock.properties['areaHectare'] = turf.area(newPaddock) * kmToHectare;
      newPaddock.properties['name'] = `${i18next.language === 'pt-BR' ? 'Piquete' : 'Paddock'} #${features.indexOf(newPaddock) + 1}`;
      newPaddock.properties['dominantPlant'] = '';
      return newPaddock;
    });
  },
  SET_DRAWN_PADDOCKS_FOR_FIELD: (state, action) => {
    if (!state.paddocksBeingDrawn?.length) {
      state.uploadingDrawnField = false;
      return;
    }

    const drawnPaddocks = [...state.paddocksBeingDrawn];
    state.paddocksBeingDrawn = [];
    state.paddocks = state.paddocks ?? {};
    state.paddocks[action.payload.id!.toString()] = drawnPaddocks;
    state.uploadingDrawnField = false;
  },
  GET_SYNC_AVAILABILITY: (state, action) => {
    if (!state.practicesOnContract || !state.fields.length) {
      return;
    }

    const country = action.payload.country;

    const practicesToValidate = Object.values(SF_PRACTICES_MAP);
    const appliedPractices: any[] = state.practicesOnContract
      .filter((i) => i.isApplied)
      .filter((i) => practicesToValidate.indexOf(i.code) >= 0)
      .map((i) => ({ name: i.code, sf: i.area, platform: 0, option: i.paymentOption }));

    const contractPaymentOptionMap = {
      OPTION_A: 'Preço fixo',
      OPTION_B: 'Porcentagem do preço de venda',
      'Preço fixo': 'Preço fixo',
      'Porcentagem do preço de venda': 'Porcentagem do preço de venda',
    };
    const paymentOptionBR = state.contractPaymentOption ? contractPaymentOptionMap[state.contractPaymentOption] : undefined;
    const areaKey = country === 'br' ? 'areaHectare' : 'area';
    const paymentOptionErrors: any = [];
    const missingPractices: string[] = [];
    const upsellingFlow = country === 'us' && action.payload.upsellingFlow;

    for (const field of state.fields.filter((i) => !i.properties?.unenrolledAt)) {
      const f: any = field;
      if (!f.properties?.practices.length && country === 'br') {
        missingPractices.push(f.id);
      }
      if (!f.properties.paymentOption && country !== 'br') {
        paymentOptionErrors.push({ id: f.id, name: f.properties.name, area: f.properties.area });
      }
      for (const practice of f.properties.practices) {
        treatPractices(f, practice, appliedPractices, areaKey, paymentOptionBR);
      }
    }

    const newPractices: any[] = [];
    const removedPractices: any[] = [];
    for (const practice of appliedPractices) {
      if (practice.sf === 0 && practice.platform > 0 && practice.option !== 'UPSELL') {
        newPractices.push(practice);
      }
      if (practice.sf > 0 && practice.platform === 0 && practice.option !== 'UPSELL') {
        removedPractices.push(practice);
      }
    }

    const practiceErrors: any = [];

    if (newPractices.length) {
      newPractices.forEach((prac) => {
        if (
          !upsellingFlow ||
          (removedPractices.some((rm) => rm.code === prac.code && rm.option !== prac.option) &&
            !removedPractices.some((rm) => rm.code === prac.code && rm.option === prac.option))
        ) {
          practiceErrors.push(prac);
        }
      });
    }

    if (removedPractices.length) {
      removedPractices.forEach((prac) => {
        if (
          !upsellingFlow ||
          (newPractices.some((newP) => newP.code === prac.code && newP.option !== prac.option) &&
            !newPractices.some((newP) => newP.code === prac.code && newP.option === prac.option))
        ) {
          practiceErrors.push(prac);
        }
      });
    }

    state.syncErrors = {
      practices: practiceErrors,
      paymentOptionErrors: paymentOptionErrors,
      missingPractices, //Only used in BR
      newPractices,
      status: practiceErrors.length || paymentOptionErrors.length ? 'ERROR' : 'OK',
    };
  },
  CHANGE_ENROLLMENT: (state, action) => {
    if (!state.fields) {
      return;
    }

    const index = state.fields.findIndex((f) => action.payload.field.id === f.id);
    state.fields[index] = {
      ...state.fields[index],
      properties: {
        ...state.fields[index].properties,
        unenrolledAt: action.payload.status ? new Date() : null,
        hidden: action.payload.hidden,
      },
    };

    state.loadingFields = false;
  },
  SET_NOT_SAVED_PADDOCK: (state) => {
    if (!state.fields || !state.paddocks) {
      return;
    }

    for (const field of state.fields) {
      if (!state.paddocks[field.id!] || state.paddocks[field.id!].length === 0) {
        state.paddocks[field.id!] = [
          {
            ...field,
            id: v4(),
            properties: {
              ...field.properties,
              fieldId: field.id,
              area: field.properties?.area,
              areaHectare: field.properties?.areaHectare,
              editing: undefined,
              selected: undefined,
              situation: undefined,
              practiceType: undefined,
              practices: undefined,
            },
          },
        ];
      } else {
        state.paddocks[field.id!] = state.paddocks[field.id!].map((paddock) => {
          return {
            ...paddock,
            id: v4(),
          };
        });
      }
    }
  },
  CHECK_FIELD_LOCATION: (state, action) => {
    state.fieldsToCheckLocation.splice(state.fieldsToCheckLocation.indexOf(action.payload), 1);
  },
  CHECK_FIELD_LOCATION_SUCCEEDED: (state, action) => {
    state.checkOverlaps = false;
    state.fields[state.fields.findIndex((f) => f.id === action.payload.id)] = action.payload;
  },
  FIELDS_VALIDATION: (state) => {
    state.fieldsToCheckIntersection = [];
  },
  FIELDS_VALIDATION_SUCCEEDED: (state, action) => {
    const fieldsValidated = action.payload.fieldsValidated.map((f) => f.id);
    state.intersections = {
      selfIntersections: [
        ...state.intersections.selfIntersections.filter((field) => !fieldsValidated.includes(field.field_id)),
        ...action.payload.intersections.selfIntersections,
      ],
      intersectionsBetweenFields: [
        ...state.intersections.intersectionsBetweenFields.filter(
          (field) => !fieldsValidated.includes(field.field_id) && !fieldsValidated.includes(field.intersects_with),
        ),
        ...action.payload.intersections.intersectionsBetweenFields,
      ],
    };

    state.fields = verifyFieldsIntersections(state.fields, state.intersections);
  },
});

export default mapReducer;
